import {
    useState,
    useEffect,
    useCallback,
    MouseEventHandler,
    ChangeEventHandler,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { TabsProps } from '@mui/material/Tabs';
import { useStore } from '@ui/hooks/useStore';
import useKeyDown from '@ui/hooks/useKeyDown';
import getLiElementId from '@utils/getLiElementId';
import encodeCol from '@utils/encodeCol';
import { checkboxClickHandler } from '@ui/components/Checkbox/checkbox-click-handler';
import { IState } from './IState';
import { position } from '@store/Map.store';
import { visibleList } from '@ui/features/Share-device-settings/visible-list';

const useGroups = () => {
    const { user, groups, devices, lang, telemetry, map, app, sidebar } =
        useStore();
    const navigate = useNavigate();
    const location = useLocation();
    const key = useKeyDown();

    const initState: IState = {
        addDialogOpen: false,
        firstLoading: true,
        locating: false,
        visibleList: {},
        addDeviceToGroupVisible: false,
        addGroupVisible: false,
        groupSettingsVisible: false,
        groupDeleteVisible: false,
        deviceDeleteVisible: false,
        deviceBtnHover: false,
        speedDialHover: false,
        filter: '',
        devSettingsCallback: () => {},
        isSettingsCloseCall: false,
    };
    const [state, setState] = useState(initState);

    const handleGroupClick: MouseEventHandler<HTMLElement> = useCallback(
        (e) => {
            setState((prev) => {
                const id = getLiElementId(e.target as HTMLElement);
                if (id) {
                    const visibleList = { ...prev.visibleList };
                    visibleList[id] = !visibleList[id];
                    //ФИКСАЦИЯ ВЫБРАННОЙ ГРУППЫ
                    let addDeviceGroupDeviceCurrent:string = id;
                    devices.setSelectedDeviceImei(addDeviceGroupDeviceCurrent);
                    //
                    return { ...prev, visibleList };
                } else return prev;
            });
        },
        [],
    );
    const handleGroupSelect: checkboxClickHandler = useCallback(
        (groupId) => {
            if (groupId && groupId !== map.selectedGroupId) {
                map.resetSelectedDevice();
                map.setSelectedGroupId(groupId);
            } else {
                map.resetSelectedGroupId();
            }
        },
        [map.selectedGroupId],
    );
    const handleDeviceSelect: checkboxClickHandler = useCallback(
        (imei) => {
            if (!imei) {
                return;
            } else if (app.page === 'history' || app.page === 'support') {
                if (map.selectedDevice !== imei) {
                    map.setSelectedDevice(imei);
                } else {
                    map.resetSelectedDevice();
                }
            } else {
                if (!map.selectedGroupId) {
                    map.setSelectedDevice(imei);
                    map.addSelectedDevices(imei);
                } else {
                    if (map.selectedDevices.indexOf(imei) > -1) {
                        map.removeSelectedDevices(imei);
                    } else {
                        map.addSelectedDevices(imei);
                    }
                }
            }
        },
        [
            app.page,
            map.selectedDevice,
            map.selectedGroupId,
            map.selectedDevices,
        ],
    );
    const handleGroupSettingsOpen: MouseEventHandler<HTMLElement> = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            const groupId = getLiElementId(e.target as HTMLElement);
            if (groupId) {
                groups.setSelectedGroupId(groupId);
                setState((prev) => ({
                    ...prev,
                    groupSettingsVisible: true,
                }));
            }
        },
        [],
    );
    const handleGroupAddDeviceOpen: MouseEventHandler<HTMLElement> = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            const groupId = getLiElementId(e.target as HTMLElement);
            if (groupId) {
                groups.setSelectedGroupId(groupId);
                setState((prev) => ({
                    ...prev,
                    addDeviceToGroupVisible: true,
                }));
            }
        },
        [],
    );
    const handleGroupAddDeviceClose = useCallback(() => {
        setState((prev) => ({
            ...prev,
            addDeviceToGroupVisible: false,
        }));
        groups.resetSelectedGroupId();
    }, []);
    const handleGroupSettingsClose = useCallback(() => {
        setState((prev) => ({
            ...prev,
            groupSettingsVisible: false,
        }));
        groups.resetSelectedGroupId();
    }, []);
    const handleDeviceClick: MouseEventHandler<HTMLElement> = useCallback(
        (e) => {
            const imei = getLiElementId(e.target as HTMLElement);
            if (!imei) return;
            if (app.page !== 'history' && app.page !== 'support') {
                if (!map.selectedGroupId) {
                    map.setSelectedDevice(imei);
                    map.addSelectedDevices(imei);
                    setState((prev) => ({ ...prev, locating: true }));
                } else {
                    const [device] = map.lastCoordsList.filter((device) => {
                        return device.imei === imei;
                    });
                    if (device) {
                        const { lat, lon } = device.gpsCoord;
                        if (lat && lon) {
                            map.setCurrentPosition([lat, lon]);
                        }
                        map.setPosition(device.gpsCoord);
                        map.setZoom(18);
                        map.setHighlightedDevice(device.imei);
                        map.setSelectedDevice(imei);
                    } else if (map.selectedGroupDevices.indexOf(imei) > -1) {
                        map.setSelectedDevice(imei);
                        setState((prev) => ({ ...prev, locating: true }));
                    }
                }
            } else if (map.selectedDevice !== imei) {
                map.setSelectedDevice(imei);
            }
        },
        [
            app.page,
            map.selectedGroupId,
            map.lastCoordsList,
            map.selectedGroupDevices,
        ],
    );
    const handleAddGroupOpen = useCallback(() => {
        setState((prev) => ({ ...prev, addGroupVisible: true }));
    }, []);
    const handleAddGroupClose = useCallback(() => {
        setState((prev) => ({ ...prev, addGroupVisible: false }));
    }, []);
    const handleAddDeviceInDbOpen = useCallback(() => {
        app.openAddDeviceInDb();
    }, []);
    const handleAddDeviceOpen = useCallback(() => {
        app.openAddDevice();
    }, []);
    const handleGroupDeleteOpen: MouseEventHandler<HTMLElement> = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            const groupId = getLiElementId(e.target as HTMLElement);
            if (groupId) {
                groups.setSelectedGroupId(groupId);
                setState((prev) => ({ ...prev, groupDeleteVisible: true }));
            }
        },
        [],
    );
    const handleGroupDeleteClose = useCallback(() => {
        setState((prev) => ({ ...prev, groupDeleteVisible: false }));
        groups.resetSelectedGroupId();
    }, []);
    const handleDeviceDeleteOpen: MouseEventHandler<HTMLElement> = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            const imei = getLiElementId(e.target as HTMLElement);
            if (imei) {
                devices.setSelectedDeviceImei(imei);
                setState((prev) => ({ ...prev, deviceDeleteVisible: true }));
            }
        },
        [],
    );
    const handleDeviceDeleteClose = useCallback(() => {
        setState((prev) => ({ ...prev, deviceDeleteVisible: false }));
        devices.resetSelectedDeviceImei();
    }, []);
    const handleDeviceDescriptionOpen: MouseEventHandler<HTMLElement> =
        useCallback((e) => {
            e.preventDefault();
            e.stopPropagation();
            const imei = getLiElementId(e.target as HTMLElement);
            if (imei) {
                devices.setEditedDeviceImei(imei);
                app.openDeviceEdit();
            }
        }, []);
    const handleDeviceTelemetryOpen: MouseEventHandler<HTMLElement> =
        useCallback(
            (e) => {
                e.preventDefault();
                e.stopPropagation();
                const imei = getLiElementId(e.target as HTMLElement);
                const [device] = devices.deviceList.filter(
                    (device) => device.imei === imei,
                );
                if (device) {
                    let coords: position | void;
                    const gpsCoord = device.gpsCoord;
                    const lbsCoord = device.lbsCoord;
                    if (gpsCoord && gpsCoord.lat && gpsCoord.lon) {
                        coords = [gpsCoord.lat, gpsCoord.lon];
                    } else if (lbsCoord && lbsCoord.lat && lbsCoord.lon) {
                        coords = [lbsCoord.lat, lbsCoord.lon];
                    }
                    if (coords) {
                        const [lat, lon] = coords;
                        map.setCurrentPosition(coords);
                        map.setPosition({ lat, lon });
                    }
                    imei && map.setSelectedDevice(imei);
                }
                if (app.page !== 'history' && app.page !== 'support') {
                    const search = new URLSearchParams(location.search);
                    const dateFilter = map.dateFilter;
                    const fromDateFilter = map.fromDateFilter.getTime();
                    const toDateFilter = map.toDateFilter.getTime();
                    let df;
                    switch (dateFilter) {
                        case 'last24Hours':
                            df = '';
                            break;
                        case 'last3Days':
                            df = '1';
                            break;
                        case 'lastWeek':
                            df = '2';
                            break;
                        case 'customDate':
                            df = '3';
                            break;
                        default:
                            df = '';
                            break;
                    }
                    const columnVisibilityModel = {
                        ...telemetry.columnVisibilityModel,
                    };
                    const [cvm, cvmKeys] = encodeCol(columnVisibilityModel);
                    search.delete('sg');
                    search.set('cvm', cvm);
                    if (imei) {
                        search.set('sd', imei);
                    } else {
                        search.delete('sd');
                    }
                    search.set('tmv', 'true');
                    search.set('df', df);
                    search.set('dff', `${fromDateFilter}`);
                    search.set('dft', `${toDateFilter}`);
                    telemetry.setTelemetryVisible();                                                           
                    navigate(`/history?${search.toString()}`);
                } else {
                    telemetry.setTelemetryVisible();
                    imei && map.setSelectedDevice(imei);
                }
            },
            [app.page, location.search],
        );
    const handleDeviceTelemetryClose = useCallback(() => {}, []);
    const handleDeviceSettingsOpen: MouseEventHandler<HTMLElement> =
        useCallback(
            (e) => {
                e.preventDefault();
                e.stopPropagation();
                const imei = getLiElementId(e.target as HTMLElement);
                const callback = () => {
                    imei && devices.setSettingDeviceImei(imei);
                    app.openDeviceSettings();
                    setState((prev) => ({
                        ...prev,
                        deviceSettingsVisible: true,
                    }));
                };
                if (!imei) return;
                const [settings] = devices.settingsList.filter(
                    (val) => val.imei === imei,
                );
                const isSettings = true;
                    //settings && settings.status && !settings.status.error;
                if (!isSettings) return;
                if (!app.deviceSettingsVisible) {
                    callback();
                } else {
                    setState((prev) => ({
                        ...prev,
                        isSettingsCloseCall: true,
                    }));
                    setState((prev) => ({
                        ...prev,
                        devSettingsCallback: callback,
                    }));
                    app.setDeviceSettingsClosing();
                }
            },
            [app.deviceSettingsVisible],
        );
    const handleDeviceSettingsClose = useCallback(() => {
        setState((prev) => ({
            ...prev,
            deviceSettingsVisible: false,
        }));
        app.closeDeviceSettings();
        devices.resetSettingDeviceImei();
    }, []);
    const handleDeviceBtnHoverStart = useCallback(() => {
        setState((prev) => ({ ...prev, deviceBtnHover: true }));
    }, []);
    const handleDeviceBtnHoverStop = useCallback(() => {
        setState((prev) => ({ ...prev, deviceBtnHover: false }));
    }, []);
    const handleSpeedDialHoverStart = useCallback(() => {
        setState((prev) => ({ ...prev, speedDialHover: true }));
    }, []);
    const handleSpeedDialHoverStop = useCallback(() => {
        setState((prev) => ({ ...prev, speedDialHover: false }));
    }, []);
    const handleSearchChange: ChangeEventHandler<HTMLInputElement> =
        useCallback((e) => {
            const filter = e.target.value;
            setState((prev) => ({ ...prev, filter }));
        }, []);
    const handleTabChange: TabsProps['onChange'] = useCallback(
        (e: React.SyntheticEvent<Element, Event>, tab: string) => {
            sidebar.setTab(tab);
        },
        [],
    );
    const handleAddDialog = useCallback(() => {
        setState((prev) => ({ ...prev, addDialogOpen: !prev.addDialogOpen }));
    }, []);
    const handleAddDiologClose = useCallback(() => {
        setState((prev) => ({ ...prev, addDialogOpen: false }));
    }, []);
    const handleKey = useCallback((key: string) => {
        switch (key) {
            case 'Escape':
                setState((prev) => ({ ...prev, addDialogOpen: false }));
                break;
        }
    }, []);
    const handleDeviceInfoOpen: MouseEventHandler<HTMLElement> = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            const imei = getLiElementId(e.target as HTMLElement);
            if (imei) {
                devices.setInfoDeviceImei(imei);
                devices.setInfoDeviceSettings(imei);
                app.openDeviceInfo();
            }
        },
        [],
    );
    const handlers = {
        handleGroupClick,
        handleGroupSelect,
        handleGroupAddDeviceOpen,
        handleGroupAddDeviceClose,
        handleGroupSettingsOpen,
        handleGroupSettingsClose,
        handleDeviceSelect,
        handleDeviceClick,
        handleAddGroupOpen,
        handleAddGroupClose,
        handleAddDeviceOpen,
        handleGroupDeleteOpen,
        handleGroupDeleteClose,
        handleDeviceDeleteOpen,
        handleDeviceDeleteClose,
        handleDeviceDescriptionOpen,
        handleDeviceTelemetryOpen,
        handleDeviceTelemetryClose,
        handleDeviceBtnHoverStart,
        handleDeviceBtnHoverStop,
        handleSearchChange,
        handleTabChange,
        handleAddDialog,
        handleAddDiologClose,
        handleDeviceSettingsOpen,
        handleAddDeviceInDbOpen,
        handleDeviceInfoOpen,
        handleSpeedDialHoverStart,
        handleSpeedDialHoverStop,
    };

    useEffect(() => {
        return () => {
            telemetry.resetTelemetryVisible();
        };
    }, []);
    useEffect(() => {
        const groupsLen = groups.groupsList.length;
        const devicesLen = devices.deviceList.length;
        if (
            state.firstLoading &&
            groupsLen > 0 &&
            devicesLen > 0 &&
            !map.selectedGroupId
        ) {
            let id = localStorage.getItem('groupId');
            if (!id) {
                [{ id }] = groups.groupsList.filter(
                    (group) => group.is_default,
                );
            }
            const listLen = devices.deviceList
                .filter((device) => device.groupId === id)
                .map(({ imei }) => imei).length;
            if (listLen > 0) {
                if (app.page !== 'history' && app.page !== 'support') {
                    map.setSelectedGroupId(id);
                }
                setState((prev) => ({ ...prev, firstLoading: false }));
            }
        }
    }, [groups.groupsList, devices.deviceList]);
    useEffect(() => {
        const groupsLen = groups.groupsList.length;
        const devicesLen = devices.deviceList.length;
        const isAddDeviceAllowed =
            user.type === 'superuser' || user.type === 'regular';
        if (
            !groups.loading &&
            !devices.loading &&
            groupsLen > 0 &&
            devicesLen === 0 &&
            isAddDeviceAllowed
        ) {
            setState((prev) => ({ ...prev, addDeviceVisible: true }));
        }
    }, [groups.groupsList, devices.deviceList, groups.loading, user.type]);
    useEffect(() => {
        if (app.page === 'history' || app.page === 'support') {
            const visibleList: visibleList = {};
            const selectedImei = map.selectedDevice;
            const [target] = devices.deviceList.filter(
                ({ imei }) => imei === selectedImei,
            );
            if (target) {
                const { groupId } = target;
                visibleList[groupId] = true;
                setState((prev) => ({ ...prev, visibleList }));
            }
        } else if (state.locating) {
            setState((prev) => ({ ...prev, locating: false }));
            const imei = map.selectedDevice;
            const [device] = map.lastCoordsList.filter(
                (device) => device.imei === imei,
            );
            if (device) map.setPosition(device.gpsCoord);
        }
    }, [map.selectedDevice, devices.deviceList]);
    useEffect(() => {
        handleKey(key);
    }, [key]);
    useEffect(() => {
        const selectedGroupId = map.selectedGroupId;
        if (selectedGroupId) {
            const visibleList: visibleList = {};
            visibleList[selectedGroupId] = true;
            setState((prev) => ({ ...prev, visibleList }));
        }
    }, [map.selectedGroupId]);
    useEffect(() => {
        if (app.isDeviceSettingsClosed && state.isSettingsCloseCall) {
            state.devSettingsCallback();
            app.resetDeviceSettingsClosed();
            setState((prev) => ({ ...prev, isSettingsCloseCall: false }));
        }
    }, [app.isDeviceSettingsClosed]);

    return {
        groups,
        devices,
        user,
        lang,
        map,
        app,
        sidebar,
        telemetry,
        state,
        handlers,
    };
};

export default useGroups;
